import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { RobuxIcon, WalletValue, walletAmount, useAuth, BB_NAME } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, Coins, Trophy, Zap, TrendingUp, ArrowDownToLine, ArrowUpFromLine, Activity, ShieldAlert, ShieldCheck, Loader2, Lock, Globe, Info, Check, Gamepad2, X, Trash2, History, Wallet, RefreshCw, Inbox, AlertCircle, Dices, Bomb, Rocket, Layers, Spade, Target, Triangle, Swords, Package, Gift, Egg, Bitcoin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { WalletModal } from '../components/WalletModal';
import { toast } from 'sonner';
import axios from 'axios';

import { useChatLayout } from '../context/ChatLayoutContext';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

/* ⚠️ KART YENİDEN KURULDU.
   Eski hâli her karta renkli bir gradyan kutu + içinde ikon koyuyordu:
   altı kart yan yana dizilince ekran altı renkli kareye dönüşüyor ve göz
   asıl bilgiyi — RAKAMI — en son buluyordu. Artık hiyerarşi doğru sırada:
   önce rakam, sonra etiket, ikon en sönük öğe. Renk yalnızca rakamın
   kendisinde ve orada BİR ŞEY SÖYLÜYOR (kâr yeşil, zarar kırmızı).

   ⚠️ `value` artık JSX kabul ediyor: tutarlar `<WalletValue>` ile
   geliyor ki para birimi simgesi doğru olsun. */
const StatCard = ({ icon: Icon, title, value, subtitle, delay, valueColor = "text-white" }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay }}
    className="mm-panel p-4 hover:border-[#3a3d43] transition-colors duration-200 flex flex-col gap-3"
  >
    <div className="flex items-center gap-2">
      <Icon className="w-3.5 h-3.5 text-zinc-600 shrink-0" />
      <h3 className="mm-eyebrow truncate">{title}</h3>
    </div>
    <div>
      <p className={`mm-num font-bold text-[19px] leading-none truncate ${valueColor}`}>{value}</p>
      {subtitle && (
        <p className="text-[10px] text-zinc-600 mt-2 truncate">{subtitle}</p>
      )}
    </div>
  </motion.div>
);

/* ============================================================
   📜 GEÇMİŞ PANELİ — SON 20 OYUN + SON 20 PARA İŞLEMİ
   ============================================================
   Veri kaynağı: GET /api/user/history   (server.py'ye eklendi)
   Yanıt şekli : { games: [...], transactions: [...] }

   Bu sitede iki ödeme rayı var ve ikisi de aynı listede gösterilir:
     • mm2    → MM2 eşya yatırma/çekme (bakiye rayı)
     • crypto → kripto yatırma/çekme

   Tüm yardımcılar bileşenin DIŞINDA: her render'da yeniden
   yaratılmasınlar diye. 20 satırlık listede fark eder.
   ============================================================ */

const HISTORY_ENDPOINT = '/api/user/history';

/* 🛡️ lucide-react sürümleri arasında ikon adları değişebiliyor.
   Var olmayan bir ikon doğrudan JSX'e girerse React "Element type is
   invalid" der ve TÜM pano çöker. Bu yüzden her ikon, bu dosyada zaten
   kullanılan (yani var olduğu KESİN) bir ikona düşerek çözümlenir. */
const safeIcon = (Icon, Fallback) => (Icon == null ? Fallback : Icon);

const IconHistory = safeIcon(History, Clock);
const IconGames = safeIcon(Gamepad2, Zap);
const IconWallet = safeIcon(Wallet, Coins);
const IconRefresh = safeIcon(RefreshCw, Loader2);
const IconEmpty = safeIcon(Inbox, Info);
const IconError = safeIcon(AlertCircle, ShieldAlert);
const IconCrypto = safeIcon(Bitcoin, Coins);
// MM2 rayı simgesi — eşya takası oyun içinde olduğu için gamepad.
const IconRobux = safeIcon(Gamepad2, Coins);

/* ---------------- Sayı biçimlendirme ---------------- */

const AMOUNT_UNITS = [
  { limit: 1e12, suffix: 'T' },
  { limit: 1e9, suffix: 'B' },
  { limit: 1e6, suffix: 'M' },
  { limit: 1e3, suffix: 'K' },
];

/* Sondaki gereksiz sıfırları atar ama SADECE ondalık varsa.
   ("2.00" -> "2", "1.50" -> "1.5", ama "100" -> "100" olarak kalır) */
const trimZeros = (text) => (text.includes('.') ? text.replace(/\.?0+$/, '') : text);

/* Tutarlar tabloya sığması için kısaltılır. Tam değer her zaman
   satırın title'ında (fare üstüne gelince) görünür. */
const formatAmount = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  for (let i = 0; i < AMOUNT_UNITS.length; i += 1) {
    const { limit, suffix } = AMOUNT_UNITS[i];
    if (abs >= limit) {
      const scaled = abs / limit;
      const text =
        scaled >= 100 ? scaled.toFixed(0) : scaled >= 10 ? scaled.toFixed(1) : scaled.toFixed(2);
      return sign + trimZeros(text) + suffix;
    }
  }
  return sign + (Number.isInteger(abs) ? String(abs) : trimZeros(abs.toFixed(2)));
};

const formatExact = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
};

/* Kripto tutarları çok küçük olabiliyor (0.00004521 BTC gibi):
   büyüklüğe göre anlamlı basamak sayısı seçilir.
   ⚠️ null/undefined AÇIKÇA elenir — Number(null) === 0 olduğu için
   sadece isFinite kontrolü yetmez. */
const formatCrypto = (value) => {
  if (value === null || value === undefined) return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return null;
  const abs = Math.abs(n);
  const digits = abs >= 1 ? 4 : abs >= 0.01 ? 6 : 8;
  return trimZeros(n.toFixed(digits));
};

/* ⚠️ Aynı null tuzağı: sunucu "usd_amount": null gönderdiğinde ekranda
   "≈ $0.00" yazardı — yani OLMAYAN bir bilgi uydurulurdu. */
const formatUsd = (value) => {
  if (value === null || value === undefined) return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return `$${n.toFixed(2)}`;
};

/* ---------------- Tarih biçimlendirme ---------------- */

const formatRelativeTime = (iso) => {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '—';
  const diff = Date.now() - t;
  if (diff < 60000) return 'just now'; // negatif fark (saat kayması) da buraya düşer
  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  const d = new Date(t);
  const opts = days >= 365
    ? { day: 'numeric', month: 'short', year: 'numeric' }
    : { day: 'numeric', month: 'short' };
  return d.toLocaleDateString(undefined, opts);
};

const formatAbsoluteTime = (iso) => {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  return new Date(t).toLocaleString();
};

/* ---------------- Oyun türü sözlüğü ---------------- */
/* Sunucudaki game_type değerleriyle BİREBİR eşleşir. */

const GAME_META = {
  dice: { label: 'Dice', icon: safeIcon(Dices, Activity) },
  mines: { label: 'Mines', icon: safeIcon(Bomb, Activity) },
  crash: { label: 'Crash', icon: safeIcon(Rocket, TrendingUp) },
  tower: { label: 'Tower', icon: safeIcon(Layers, Activity) },
  plinko: { label: 'Plinko', icon: safeIcon(Triangle, Activity) },
  roulette: { label: 'Roulette', icon: safeIcon(Target, Activity) },
  blackjack: { label: 'Blackjack', icon: safeIcon(Spade, Activity) },
  coinflip: { label: 'Coinflip', icon: Coins },
  jackpot: { label: 'Jackpot', icon: Trophy },
  case_opening: { label: 'Case Opening', icon: safeIcon(Package, Activity) },
  level_case_open: { label: 'Level Case', icon: safeIcon(Gift, Trophy) },
  case_battle: { label: 'Case Battle', icon: safeIcon(Swords, Activity) },
  chicken_road: { label: 'Chicken Road', icon: safeIcon(Egg, Activity) },
};

/* Sözlükte olmayan bir tür gelirse (yeni oyun eklenirse) çökme yerine
   okunabilir bir etikete dönüştürülür: "space_dice" -> "Space Dice" */
const prettifyKey = (key) =>
  String(key || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();

const getGameMeta = (type) => {
  const meta = GAME_META[type];
  if (meta) return meta;
  return { label: prettifyKey(type) || 'Unknown', icon: IconGames };
};

/* ---------------- İşlem durumu sözlüğü ---------------- */
/* Sunucudaki TÜM statüler burada karşılanır: hem P2P (pending_p2p,
   pending_payment, processing_p2p, needs_review, on_hold, forfeit*)
   hem kripto (creating, awaiting_payment, confirming, crediting…). */

const STATUS_META = {
  completed: { label: 'Completed', tone: 'success' },
  // --- P2P (Roblox Gamepass) ---
  pending_p2p: { label: 'Listed', tone: 'active' },
  pending_payment: { label: 'Awaiting payment', tone: 'active' },
  processing_p2p: { label: 'Processing', tone: 'active' },
  needs_review: { label: 'In review', tone: 'review' },
  on_hold: { label: 'On hold', tone: 'review' },
  forfeit_pending: { label: 'Forfeit pending', tone: 'danger' },
  forfeited: { label: 'Forfeited', tone: 'danger' },
  // --- Kripto ---
  awaiting_payment: { label: 'Awaiting payment', tone: 'active' },
  confirming: { label: 'Confirming', tone: 'active' },
  crediting: { label: 'Crediting', tone: 'active' },
  reserving: { label: 'Reserving', tone: 'active' },
  creating: { label: 'Creating', tone: 'active' },
  pending_approval: { label: 'Awaiting approval', tone: 'review' },
  // --- Ortak ---
  pending: { label: 'Pending', tone: 'active' },
  processing: { label: 'Processing', tone: 'active' },
  manual_review: { label: 'In review', tone: 'review' },
  cancelled: { label: 'Cancelled', tone: 'muted' },
  expired: { label: 'Expired', tone: 'muted' },
  refunded: { label: 'Refunded', tone: 'muted' },
  failed: { label: 'Failed', tone: 'danger' },
};

const STATUS_TONES = {
  success: { chip: 'text-[#2ecf6e] bg-[#16A34A]/10 border-[#16A34A]/25', dot: 'bg-[#2ecf6e]' },
  active: { chip: 'text-[#c98bf0] bg-[#B059E8]/10 border-[#B059E8]/25', dot: 'bg-[#B059E8]' },
  review: { chip: 'text-amber-300 bg-amber-500/10 border-amber-500/25', dot: 'bg-amber-400' },
  muted: { chip: 'text-zinc-400 bg-zinc-500/10 border-zinc-500/25', dot: 'bg-zinc-500' },
  danger: { chip: 'text-rose-300 bg-rose-500/10 border-rose-500/25', dot: 'bg-rose-400' },
};

const getStatusMeta = (status) =>
  STATUS_META[status] || { label: prettifyKey(status) || 'Unknown', tone: 'muted' };

const StatusChip = ({ status }) => {
  const meta = getStatusMeta(status);
  const tone = STATUS_TONES[meta.tone] || STATUS_TONES.muted;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[10px] font-bold uppercase tracking-wider whitespace-nowrap ${tone.chip}`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${tone.dot} ${meta.tone === 'active' ? 'animate-pulse' : ''}`}
      />
      {meta.label}
    </span>
  );
};

/* ---------------- Satır: OYUN ---------------- */

const GameRow = ({ game, index, reduceMotion }) => {
  const meta = getGameMeta(game.game_type);
  const Icon = meta.icon;
  const net = Number(game.net) || 0;
  const isProfit = net > 0;
  const isPush = net === 0;
  const multiplier = Number(game.multiplier) || 0;

  const netColor = isProfit ? 'text-[#2ecf6e]' : isPush ? 'text-zinc-300' : 'text-rose-400';
  const netPrefix = isProfit ? '+' : isPush ? '' : '-';

  return (
    <motion.div
      initial={reduceMotion ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: reduceMotion ? 0 : Math.min(index * 0.02, 0.25) }}
      className="flex items-center gap-3 rounded-xl border border-transparent px-2 py-2.5 transition-colors hover:border-[#2A2D31] hover:bg-[#15171a] sm:gap-4 sm:px-3"
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <div
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border ${
            isProfit
              ? 'border-[#16A34A]/25 bg-[#16A34A]/10 text-[#2ecf6e]'
              : 'border-[#2A2D31] bg-[#15171a] text-zinc-400'
          }`}
        >
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <p className="truncate font-body text-sm font-semibold text-white">{meta.label}</p>
          <p
            className="truncate font-body text-[11px] text-zinc-500"
            title={formatAbsoluteTime(game.created_at)}
          >
            {formatRelativeTime(game.created_at)}
            {/* Mobilde ayrı sütun yok; bahis ve çarpan buraya katlanır */}
            <span className="sm:hidden">
              {' · '}
              {/* Geçmişte iki cüzdanın oyunları karışık gelir. */}
              <WalletValue amount={game.bet_amount} wallet={game.wallet} /> · ×{multiplier.toFixed(2)}
            </span>
          </p>
        </div>
      </div>

      <div className="hidden w-24 shrink-0 text-right sm:block">
        <p className="mm-num text-sm text-zinc-300" title={walletAmount(game.bet_amount, game.wallet)}>
          <WalletValue amount={game.bet_amount} wallet={game.wallet} />
        </p>
      </div>

      <div className="hidden w-20 shrink-0 justify-center sm:flex">
        <span
          className={`rounded-md border px-2 py-1 mm-num text-xs font-bold ${
            multiplier >= 1
              ? 'border-[#B059E8]/30 bg-[#B059E8]/10 text-[#c98bf0]'
              : 'border-[#2A2D31] bg-[#15171a] text-zinc-500'
          }`}
        >
          ×{multiplier.toFixed(2)}
        </span>
      </div>

      <div className="w-24 shrink-0 text-right sm:w-28">
        <p
          className={`mm-num text-sm font-bold ${netColor}`}
          title={`${netPrefix}${formatExact(Math.abs(net))} $`}
        >
          {netPrefix}
          {formatAmount(Math.abs(net))}<RobuxIcon size={12} className="ml-1" />
        </p>
      </div>
    </motion.div>
  );
};

/* ---------------- Satır: PARA İŞLEMİ ---------------- */

/* Ana tutar, kullanıcının bakiyesine YANSIYAN rakamdır:
     • yatırma → site_credit_amount (bonus dahil hesaba geçen)
     • çekim   → amount (bakiyeden düşen)
   Yatırmada site_credit_amount yoksa amount'a düşülür. */
const mainAmountOf = (tx) => {
  if (tx.kind === 'deposit' && tx.site_credit_amount !== null && tx.site_credit_amount !== undefined) {
    return Number(tx.site_credit_amount) || 0;
  }
  return Number(tx.amount) || 0;
};

/* İşlemin ikinci satırındaki ayrıntı metnini kurar.
   ⚠️ Sadece sunucudan GERÇEKTEN gelen alanlar yazılır; eksik olan
   bilgi uydurulmaz, o parça hiç görünmez. */
const buildTransactionDetail = (tx) => {
  const parts = [];

  if (tx.method === 'crypto') {
    const coin = tx.asset ? String(tx.asset).toUpperCase() : null;
    const cryptoAmount = formatCrypto(tx.crypto_amount);
    if (cryptoAmount && coin) parts.push(`${cryptoAmount} ${coin}`);
    else if (coin) parts.push(coin);

    const usd = formatUsd(tx.usd_amount);
    if (usd) parts.push(`≈ ${usd}`);

    // Ağ, coin kodundan farklıysa anlamlı bilgi taşır (ör. USDT / TRX)
    const network = tx.network ? String(tx.network).toUpperCase() : null;
    if (network && network !== coin) parts.push(`${network} network`);
  } else {
    /* ⚠️ ROBUX ALANLARI ARTIK GELMİYOR. Eski hâli `estimated_net_robux`
       okuyordu; o alanı üreten P2P rayı söküldü ve alan hiçbir kayıtta
       yok — dal sessizce boş kalıyordu.
       MM2 rayı bunun yerine hangi eşyaların işlem gördüğünü ve o günkü
       kuru bildiriyor. Kur AYRICA yazılır: admin kuru değiştirebiliyor ve
       kullanıcı geçmişine baktığında O ANKİ kuru görmeli, bugünküyle
       yeniden hesaplanmış bir rakamı değil. */
    const names = Array.isArray(tx.items) ? tx.items.filter(Boolean) : [];
    if (names.length) {
      parts.push(names.length <= 2
        ? names.join(' + ')
        : `${names.slice(0, 2).join(' + ')} +${names.length - 2}`);
    }
    const val = Number(tx.item_value);
    if (Number.isFinite(val) && val > 0) parts.push(`${formatAmount(val)} item value`);
    const legs = Number(tx.legs);
    if (Number.isFinite(legs) && legs > 1) parts.push(`${legs} bots`);
  }

  return parts.join(' · ');
};

const TransactionRow = ({ tx, index, reduceMotion }) => {
  const isDeposit = tx.kind === 'deposit';
  const isCrypto = tx.method === 'crypto';
  const DirectionIcon = isDeposit ? ArrowDownToLine : ArrowUpFromLine;
  const MethodIcon = isCrypto ? IconCrypto : IconRobux;
  const amount = mainAmountOf(tx);
  const detail = buildTransactionDetail(tx);

  // Kısmen dolan P2P çekimlerde ilerleme çubuğu gösterilir.
  const progress = Number(tx.progress_percent);
  const showProgress =
    !isCrypto && !isDeposit && Number.isFinite(progress) && progress > 0 && progress < 100;

  return (
    <motion.div
      initial={reduceMotion ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: reduceMotion ? 0 : Math.min(index * 0.02, 0.25) }}
      className="rounded-xl border border-transparent px-2 py-2.5 transition-colors hover:border-[#2A2D31] hover:bg-[#15171a] sm:px-3"
    >
      <div className="flex items-center gap-3 sm:gap-4">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <div
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border ${
              isDeposit
                ? 'border-[#16A34A]/25 bg-[#16A34A]/10 text-[#2ecf6e]'
                : 'border-rose-500/25 bg-rose-500/10 text-rose-400'
            }`}
          >
            <DirectionIcon className="h-4 w-4" />
          </div>

          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="font-body text-sm font-semibold text-white">
                {isDeposit ? 'Deposit' : 'Withdraw'}
              </p>
              <span
                className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${
                  isCrypto
                    ? 'border-amber-500/25 bg-amber-500/10 text-amber-300'
                    : 'border-[#B059E8]/25 bg-[#B059E8]/10 text-[#c98bf0]'
                }`}
              >
                <MethodIcon className="h-2.5 w-2.5" />
                {isCrypto ? 'Crypto' : 'MM2'}
              </span>
            </div>
            <p
              className="truncate font-body text-[11px] text-zinc-500"
              title={formatAbsoluteTime(tx.created_at)}
            >
              {formatRelativeTime(tx.created_at)}
              {detail ? ` · ${detail}` : ''}
            </p>
          </div>
        </div>

        <div className="shrink-0 text-right">
          <p
            className={`mm-num text-sm font-bold ${isDeposit ? 'text-[#2ecf6e]' : 'text-rose-400'}`}
            /* ⚠️ Kripto işlemleri BB Coin cüzdanına yazılıyor ve tutarları
               MİLİ-LC cinsinden; `wallet` alanı birimi söyler. */
            title={walletAmount(amount, tx.wallet)}
          >
            {isDeposit ? '+' : '-'}
            <WalletValue amount={amount} wallet={tx.wallet} />
          </p>
          <div className="mt-1 flex justify-end sm:hidden">
            <StatusChip status={tx.status} />
          </div>
        </div>

        <div className="hidden w-36 shrink-0 justify-end sm:flex">
          <StatusChip status={tx.status} />
        </div>
      </div>

      {showProgress && (
        <div className="mt-2 pl-12">
          <div className="mb-1 flex items-center justify-between">
            <span className="font-body text-[10px] uppercase tracking-wider text-zinc-600">
              Filled
            </span>
            <span className="mm-num text-[10px] text-zinc-400">
              {formatAmount(tx.completed_amount)} / {formatAmount(tx.amount)} (
              {progress.toFixed(1)}%)
            </span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full border border-[#2A2D31] bg-[#15171a]">
            <div
              className="h-full rounded-full bg-gradient-to-r from-[#B059E8] to-[#c98bf0]"
              style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
            />
          </div>
        </div>
      )}
    </motion.div>
  );
};

/* ---------------- Yükleniyor / boş / hata durumları ---------------- */

const HistorySkeleton = () => (
  <div className="space-y-1">
    {[0, 1, 2, 3, 4].map((i) => (
      <div key={i} className="flex items-center gap-3 px-2 py-2.5 sm:px-3">
        <div className="h-9 w-9 shrink-0 animate-pulse rounded-lg bg-[#15171a]" />
        <div className="flex-1 space-y-2">
          <div className="h-3 w-24 animate-pulse rounded bg-[#15171a]" />
          <div className="h-2.5 w-32 animate-pulse rounded bg-[#15171a]" />
        </div>
        <div className="h-3 w-16 animate-pulse rounded bg-[#15171a]" />
      </div>
    ))}
  </div>
);

const HistoryEmpty = ({ tab, onAction }) => (
  <div className="flex flex-col items-center justify-center px-4 py-12 text-center">
    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl border border-[#2A2D31] bg-[#15171a]">
      <IconEmpty className="h-5 w-5 text-zinc-600" />
    </div>
    <h4 className="font-heading text-sm font-bold uppercase tracking-wider text-zinc-300">
      {tab === 'games' ? 'No rounds yet' : 'No transactions yet'}
    </h4>
    <p className="mt-1.5 max-w-xs font-body text-xs text-zinc-500">
      {tab === 'games'
        ? 'Your last 20 rounds will show up here once you start playing.'
        : 'Your last 20 deposits and withdrawals will show up here.'}
    </p>
    {tab === 'transactions' && (
      <Button
        onClick={onAction}
        className="mt-4 bg-gradient-to-r from-[#B059E8] to-[#7b2fc4] px-6 font-heading text-xs font-bold uppercase tracking-wider text-white"
      >
        Make a deposit
      </Button>
    )}
  </div>
);

const HistoryError = ({ onRetry, retrying }) => (
  <div className="flex flex-col items-center justify-center px-4 py-12 text-center">
    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl border border-rose-500/25 bg-rose-500/10">
      <IconError className="h-5 w-5 text-rose-400" />
    </div>
    <h4 className="font-heading text-sm font-bold uppercase tracking-wider text-zinc-300">
      History didn't load
    </h4>
    <p className="mt-1.5 max-w-xs font-body text-xs text-zinc-500">
      The connection dropped before your activity came through.
    </p>
    <Button
      onClick={onRetry}
      disabled={retrying}
      variant="outline"
      className="mt-4 border-[#2A2D31] px-6 font-heading text-xs font-bold uppercase tracking-wider text-[#c98bf0] hover:bg-[#B059E8]/10"
    >
      {retrying ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Try again'}
    </Button>
  </div>
);

/* ---------------- Panel ---------------- */

const HISTORY_TABS = [
  { key: 'games', label: 'Games', icon: IconGames },
  { key: 'transactions', label: 'Transactions', icon: IconWallet },
];

const ActivityPanel = ({
  status,
  games,
  transactions,
  activeTab,
  onTabChange,
  onRefresh,
  refreshing,
  onDeposit,
  reduceMotion,
}) => {
  const rows = activeTab === 'games' ? games : transactions;
  const isLoading = status === 'loading';
  const isError = status === 'error';

  return (
    <motion.div
      initial={reduceMotion ? false : { opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.35 }}
      className="md:col-span-12 mt-2 rounded-2xl border border-[#2A2D31] bg-[#1A1D20] p-4 sm:p-6"
    >
      <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#B059E8] to-[#7b2fc4]">
            <IconHistory className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="font-heading text-lg font-bold uppercase tracking-wide text-white">
              Recent Activity
            </h2>
            <p className="font-body text-xs text-zinc-500">Your last 20 rounds and transactions</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div
            role="tablist"
            aria-label="Recent activity"
            className="flex flex-1 gap-1 rounded-xl border border-[#2A2D31] bg-[#15171a] p-1 lg:flex-none"
          >
            {HISTORY_TABS.map((tab) => {
              const TabIcon = tab.icon;
              const isActive = activeTab === tab.key;
              const count = tab.key === 'games' ? games.length : transactions.length;
              return (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => onTabChange(tab.key)}
                  className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 font-heading text-xs font-bold uppercase tracking-wider transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B059E8] lg:flex-none ${
                    isActive ? 'bg-[#B059E8]/15 text-[#c98bf0]' : 'text-zinc-500 hover:text-zinc-300'
                  }`}
                >
                  <TabIcon className="h-3.5 w-3.5" />
                  {tab.label}
                  {!isLoading && !isError && count > 0 && (
                    <span
                      className={`rounded px-1.5 py-0.5 mm-num text-[10px] ${
                        isActive ? 'bg-[#B059E8]/20 text-[#c98bf0]' : 'bg-[#2A2D31] text-zinc-500'
                      }`}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <button
            type="button"
            onClick={onRefresh}
            disabled={refreshing || isLoading}
            aria-label="Refresh activity"
            title="Refresh"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#2A2D31] bg-[#15171a] text-zinc-400 transition-colors hover:border-[#B059E8]/30 hover:text-[#c98bf0] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B059E8] disabled:opacity-50"
          >
            <IconRefresh className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {isLoading ? (
        <HistorySkeleton />
      ) : isError ? (
        <HistoryError onRetry={onRefresh} retrying={refreshing} />
      ) : rows.length === 0 ? (
        <HistoryEmpty tab={activeTab} onAction={onDeposit} />
      ) : (
        <div>
          {activeTab === 'games' && (
            <div className="mb-1 hidden items-center gap-4 border-b border-[#2A2D31] px-3 pb-2 sm:flex">
              <span className="flex-1 font-body text-[10px] font-bold uppercase tracking-wider text-zinc-600">
                Game
              </span>
              <span className="w-24 text-right font-body text-[10px] font-bold uppercase tracking-wider text-zinc-600">
                Bet
              </span>
              <span className="w-20 text-center font-body text-[10px] font-bold uppercase tracking-wider text-zinc-600">
                Multiplier
              </span>
              <span className="w-28 text-right font-body text-[10px] font-bold uppercase tracking-wider text-zinc-600">
                Profit
              </span>
            </div>
          )}

          <div className="space-y-0.5">
            {activeTab === 'games'
              ? rows.map((game, i) => (
                  <GameRow
                    key={game.id || `game-${i}`}
                    game={game}
                    index={i}
                    reduceMotion={reduceMotion}
                  />
                ))
              : rows.map((tx, i) => (
                  <TransactionRow
                    key={tx.id || `tx-${i}`}
                    tx={tx}
                    index={i}
                    reduceMotion={reduceMotion}
                  />
                ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};

/* Sistem "hareketi azalt" ayarını okur.
   framer-motion'un useReducedMotion'ı yerine bunu kullanıyoruz ki
   projedeki framer-motion sürümüne bağımlılık doğmasın. */
const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(query.matches);
    const onChange = (event) => setReduced(event.matches);
    // Safari <14 addEventListener desteklemiyor → addListener'a düş
    if (query.addEventListener) {
      query.addEventListener('change', onChange);
      return () => query.removeEventListener('change', onChange);
    }
    query.addListener(onChange);
    return () => query.removeListener(onChange);
  }, []);
  return reduced;
};

export const Dashboard = () => {
  // 💬 Aç/kapa durumu paylaşımlı: sayfa değişse de korunur.
  const { chatOpen } = useChatLayout();
  const { user, token, isAuthenticated, loading, balanceChange, refreshUser, balance, fmtMoney, wallet } = useAuth();

  // ⚠️ Kaydettikten sonra TÜM SAYFA yeniden yükleniyordu (window.location.reload).
  // Sunucu 200'ü hızlı dönse bile kullanıcı sayfanın baştan kurulmasını (auth,
  // stats, websocket, tüm avatarlar) bekliyordu — "butona bastım, cevap gelmiyor"
  // hissinin asıl kaynağı buydu, backend değil.
  // Artık sonuç YERELDE uygulanır: ekran anında güncellenir.
  const [robloxOverride, setRobloxOverride] = useState(undefined);
  /* ⚠️ ARTIK HİÇ TETİKLENMİYOR — ama KALDIRILMADI, çünkü kaldırmak iki
     karta dokunup görsel bir regresyon riski doğurur.
     Tek tetikleyicisi Open Cloud sihirbazındaki "bu ben değilim" akışıydı
     (`scrollToRobloxCard`); o ray MM2'ye geçişle söküldü. Kart yine
     çalışıyor, sadece vurgu animasyonu hiç görünmüyor.
     Roblox kartına yeniden bir "buraya bak" akışı eklenirse setter zaten
     hazır. Gereksiz bulunursa iki karttaki koşullu sınıfla birlikte
     temizlenebilir. */
  const [robloxHighlight, setRobloxHighlight] = useState(false);
  void setRobloxHighlight;
  const robloxName = robloxOverride !== undefined ? robloxOverride : user?.roblox_username;
  const navigate = useNavigate();

  const [walletOpen, setWalletOpen] = useState(false);
  const [stats, setStats] = useState(null);

  // Roblox kullanıcı adı (opsiyonel, kozmetik — DOĞRULAMA YOK, sadece profil avatarı)
  const [robloxModal, setRobloxModal] = useState(false);
  const [robloxInput, setRobloxInput] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [robloxPreview, setRobloxPreview] = useState(null);
  const [robloxChecking, setRobloxChecking] = useState(false);
  const [previewImgFailed, setPreviewImgFailed] = useState(false);
  const lookupCache = useRef(new Map());
  const [avatarFailed, setAvatarFailed] = useState(false);

  // LiveChat'teki `getAvatarColor` ile AYNI palet ve AYNI formül.
  // Farklı olsaydı aynı kullanıcı sohbette mor, dashboard'da mavi görünürdü.
  const getAvatarColor = (username) => {
    const colors = [
      'from-purple-500 to-rose-500', 'from-purple-500 to-indigo-500',
      'from-blue-500 to-cyan-500', 'from-green-500 to-green-500',
      'from-orange-500 to-amber-500', 'from-red-500 to-purple-500',
    ];
    return colors[username?.charCodeAt(0) % colors.length || 0];
  };

  // YENİ: Privacy (Gizlilik) State'i
  const [isPrivate, setIsPrivate] = useState(false);
  const [privacyLoading, setPrivacyLoading] = useState(false);

  // YENİ: Geçmiş (son 20 oyun / son 20 para işlemi) State'leri
  const [history, setHistory] = useState({ games: [], transactions: [] });
  const [historyStatus, setHistoryStatus] = useState('loading'); // loading | ready | error
  const [historyTab, setHistoryTab] = useState('games');
  const [historyRefreshing, setHistoryRefreshing] = useState(false);
  const reduceMotion = usePrefersReducedMotion();

  // Hızlı arka arkaya yenilemede ESKİ cevabın YENİ cevabı ezmesini engeller.
  const historyRequestId = useRef(0);
  const historyMounted = useRef(true);

  useEffect(() => {
    historyMounted.current = true;
    return () => {
      historyMounted.current = false;
    };
  }, []);

  const fetchHistory = useCallback(
    async (options) => {
      const silent = Boolean(options && options.silent);
      if (!token) return;

      const requestId = historyRequestId.current + 1;
      historyRequestId.current = requestId;

      if (silent) setHistoryRefreshing(true);
      else setHistoryStatus('loading');

      try {
        const res = await axios.get(`${BACKEND_URL}${HISTORY_ENDPOINT}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        // Sadece EN SON istek state'i yazabilir.
        if (requestId !== historyRequestId.current || !historyMounted.current) return;

        const data = res.data || {};
        setHistory({
          games: Array.isArray(data.games) ? data.games : [],
          transactions: Array.isArray(data.transactions) ? data.transactions : [],
        });
        setHistoryStatus('ready');
      } catch (error) {
        if (requestId !== historyRequestId.current || !historyMounted.current) return;
        setHistoryStatus('error');
      } finally {
        if (requestId === historyRequestId.current && historyMounted.current) {
          setHistoryRefreshing(false);
        }
      }
    },
    [token]
  );

  useEffect(() => {
    if (isAuthenticated && token) fetchHistory();
  }, [isAuthenticated, token, fetchHistory]);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/');
      window.dispatchEvent(new CustomEvent('donut:auth', { detail: { tab: 'login' } }));
    }
  }, [loading, isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated && token) {
      axios.get(`${BACKEND_URL}/api/game/stats`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setStats(res.data)).catch(() => {});
    }
  }, [isAuthenticated, token]);

  // Sayfa yüklendiğinde kullanıcının mevcut gizlilik ayarını çek
  useEffect(() => {
    if (user?.is_profile_private !== undefined) {
      setIsPrivate(user.is_profile_private);
    }
  }, [user]);

  // Roblox adı canlı avatar önizlemesi (opsiyonel — modal açıkken debounce'lu)
  useEffect(() => {
    if (!robloxModal) return;
    const v = robloxInput.trim();
    if (v.length < 3) { setRobloxPreview(null); return; }
    // ⚠️ AYNI İSMİ TEKRAR SORMA. Kullanıcı yazıp silip aynı yere dönerse
    // (çok olur) her seferinde yeni istek gidiyordu. Sonuçları yerelde
    // tutmak hem hız kazandırır hem sunucu limitini korur.
    if (lookupCache.current.has(v)) {
      setPreviewImgFailed(false);
      setRobloxPreview(lookupCache.current.get(v));
      return;
    }

    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      setRobloxChecking(true);
      try {
        const res = await axios.post(`${BACKEND_URL}/api/roblox/lookup`,
          { roblox_username: v }, { signal: ctrl.signal });
        lookupCache.current.set(v, res.data);
        setPreviewImgFailed(false);
        setRobloxPreview(res.data);
      } catch (err) {
        // İptal edilen istek hata değildir; kullanıcı yazmaya devam etmiştir.
        if (!axios.isCancel?.(err) && err?.name !== 'CanceledError') {
          setRobloxPreview(null);
        }
      }
      finally { setRobloxChecking(false); }
    }, 600);
    // Kullanıcı yazmaya devam ederse ESKİ İSTEK İPTAL EDİLİR. Yoksa geç dönen
    // eski cevap, yeni cevabın üstüne yazıp yanlış avatarı gösterebilirdi.
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [robloxInput, robloxModal]);

  // Context tazelemesi varsa arkada çalışır; ekran zaten güncel.
  const syncUser = () => { if (typeof refreshUser === 'function') refreshUser(); };


  const openRobloxModal = () => {
    setRobloxInput(robloxName || '');
    setRobloxPreview(null);
    setRobloxModal(true);
  };

  // Roblox adını kaydet (backend Roblox'tan çözer; çözülemese de ad kaydedilir, avatar varsayılan kalır)
  const handleSetRoblox = async () => {
    const v = robloxInput.trim();
    if (!v) return toast.error("Please enter your Roblox username.");
    setActionLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/user/roblox-username`,
        { roblox_username: v },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data?.message || "Roblox username saved!");
      // Backend kanonik adı döner (büyük/küçük harfi düzeltilmiş olabilir).
      setRobloxOverride(res.data?.roblox_username || v);
      setAvatarFailed(false);   // yeni hesap → avatarı tekrar dene
      setRobloxModal(false);
      syncUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || "An error occurred");
    } finally {
      setActionLoading(false);
    }
  };

  // Roblox adını kaldır (null gönder → ad + avatar id sıfırlanır)
  const handleClearRoblox = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/user/roblox-username`,
        { roblox_username: null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Roblox username removed.");
      setRobloxOverride(null);
      setRobloxModal(false);
      syncUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || "An error occurred");
    } finally {
      setActionLoading(false);
    }
  };

  // YENİ: Gizlilik Ayarını Değiştirme Fonksiyonu
  const togglePrivacy = async () => {
    setPrivacyLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/user/privacy`, { is_private: !isPrivate }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsPrivate(!isPrivate);
      toast.success(res.data.message);
    } catch (error) {
      toast.error("Failed to update privacy settings");
    } finally {
      setPrivacyLoading(false);
    }
  };

  if (loading || !user) return <div className="min-h-screen flex items-center justify-center bg-[#0E0F11]"><Loader2 className="w-8 h-8 animate-spin text-[#B059E8]" /></div>;

  const currentXp = Number(user.xp) || 0;
  const currentLevel = Number(user.level) || 1;
  const requiredXp = Math.floor(Math.pow(currentLevel, 1.9) * 50);
  const levelProgress = Math.min(100, (currentXp / requiredXp) * 100);
  const remainingXp = Math.max(0, requiredXp - currentXp);
  const winRate = stats?.total_games > 0 ? ((stats.total_wins / stats.total_games) * 100).toFixed(1) : 0;

  return (
    <div
      /* 💬 Sohbet açıkken içerik sağa kayar; aksi halde panel sayfanın
         ÜSTÜNE biner ve soldaki sütun okunamaz hale gelir. */
      className={`min-h-screen bg-[#0E0F11] pt-24 pb-12 px-4 transition-[padding] duration-300 ${
        chatOpen ? 'md:pl-[340px] lg:pl-[370px]' : ''
      }`}
      data-testid="dashboard-page"
    >
      <div className="fixed top-40 left-10 w-96 h-96 bg-[#B059E8]/5 rounded-full blur-[150px] pointer-events-none" />
      
      <div className="max-w-7xl mx-auto relative z-10">
        
        {/* ÜST KISIM: KARŞILAMA VE GİZLİLİK AYARI */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="font-heading font-bold text-3xl md:text-4xl text-white uppercase tracking-wide mb-2">
              Welcome, <span className="text-[#c98bf0]">{user.site_username}</span> 🍩
            </h1>
            <p className="font-body text-zinc-400">Welcome to your account dashboard</p>
          </motion.div>

          {/* YENİ: GİZLİLİK (PRIVACY) KARTI */}
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="flex items-center gap-4 bg-[#1A1D20] border border-[#2A2D31] rounded-xl p-4 shadow-lg">
            <div className={`p-2 rounded-lg ${isPrivate ? 'bg-rose-500/20 text-rose-500' : 'bg-[#16A34A]/20 text-[#16A34A]'}`}>
              {isPrivate ? <Lock className="w-5 h-5" /> : <Globe className="w-5 h-5" />}
            </div>
            <div className="flex flex-col mr-4">
              <span className="text-white font-bold text-sm">Profile Privacy</span>
              <span className="text-zinc-500 text-xs">{isPrivate ? 'Your stats are hidden' : 'Your stats are public'}</span>
            </div>
            <button
              onClick={togglePrivacy}
              disabled={privacyLoading}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors focus:outline-none ${isPrivate ? 'bg-rose-500' : 'bg-zinc-600'}`}
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${isPrivate ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          
          {/* ROBLOX KULLANICI ADI (opsiyonel · kozmetik) */}
          {/* id: kurulum sihirbazındaki "bu ben değilim" bağlantısının hedefi.
              Kullanıcı zaten bu sayfada olduğu için yönlendirmek anlamsız —
              doğru davranış ilgili kartı gösterip vurgulamak. */}
          {!robloxName ? (
            <motion.div
              id="roblox-username-card"
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
              className={`md:col-span-12 bg-[#B059E8]/10 border rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-6 transition-all duration-500 ${
                robloxHighlight ? 'border-[#B059E8] ring-2 ring-[#B059E8]/50' : 'border-[#B059E8]/30'
              }`}>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#B059E8]/20 rounded-full flex items-center justify-center shrink-0">
                  <Gamepad2 className="w-6 h-6 text-[#c98bf0]" />
                </div>
                <div>
                  <h2 className="text-white font-bold text-lg">Link Your Roblox Username</h2>
                  <p className="text-[#d29bff]/70 text-sm mt-1">Optional — set your Roblox username to show your avatar on your profile.</p>
                </div>
              </div>
              <Button onClick={openRobloxModal} className="bg-[#7b2fc4] hover:bg-[#B059E8] text-white font-bold px-8 shadow-lg shadow-[#B059E8]/20">
                Add Roblox Username
              </Button>
            </motion.div>
          ) : (
            /* Aynı id iki dalda da var; her seferinde yalnızca BİRİ render
               edilir, yani çakışma olmaz. Asıl "hesabımı değiştireceğim"
               senaryosu bu dal — id'yi sadece diğerine koymak, tam da hedef
               kullanıcıyı ıskalardı. */
            <motion.div
              id="roblox-username-card"
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
              className={`md:col-span-12 bg-[#16A34A]/10 border rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-6 transition-all duration-500 ${
                robloxHighlight ? 'border-[#16A34A] ring-2 ring-[#16A34A]/50' : 'border-[#16A34A]/30'
              }`}>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#121417] border border-[#16A34A]/30 overflow-hidden flex items-center justify-center shrink-0">
                  {/* Roblox bağlı DEĞİLSE hiç istek atılmaz; baş harf yerelde
                      çizilir. Bağlıysa denenir, yüklenmezse yine baş harfe
                      düşülür — boş kutu bırakmak en kötüsü. */}
                  {robloxName && !avatarFailed ? (
                    <img
                      src={`${BACKEND_URL}/api/user/avatar/${user.site_username}?v=${encodeURIComponent(robloxName || '')}`}
                      alt="" className="w-full h-full object-cover"
                      onError={() => setAvatarFailed(true)}
                    />
                  ) : (
                    <div className={`w-full h-full flex items-center justify-center bg-gradient-to-br ${getAvatarColor(user.site_username)}`}>
                      <span className="text-white font-bold text-lg">
                        {user.site_username?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>
                <div>
                  <h2 className="text-[#2ecf6e] font-bold text-lg">Roblox Linked</h2>
                  <p className="text-[#86efac]/70 text-sm mt-1">Roblox Username: <strong className="text-white bg-[#16A34A]/20 px-2 py-0.5 rounded ml-1">{robloxName}</strong></p>
                </div>
              </div>
              <Button onClick={openRobloxModal} variant="outline" className="border-[#16A34A]/40 text-[#2ecf6e] hover:bg-[#16A34A]/10 font-bold px-8">
                Change
              </Button>
            </motion.div>
          )}


          {/* ══════════════════════════════════════════════════════════════
              BAKİYE KARTI
              ⚠️ GRADYANLI İKON KUTUSU KALDIRILDI. Kartın işi tek bir şeyi
              söylemek: ne kadar paran var. Yanına 56px'lik mor bir kutu
              koymak, o rakamla dikkat için yarışan ikinci bir öğe yaratmak
              demekti. Şimdi rakam kartın tek yıldızı; imza tayfı üst kenarda
              duruyor ve kartın önemini rengiyle değil KONUMUYLA söylüyor.
              ══════════════════════════════════════════════════════════════ */}
          <motion.div className="md:col-span-8 mm-panel mm-chroma-edge p-6 sm:p-8 relative overflow-hidden flex flex-col justify-between">
            <div className="flex items-center justify-between gap-3">
              <span className="mm-eyebrow">Total balance</span>
              <span className="text-[10px] text-zinc-600 inline-flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#2ecf6e]" />
                Live
              </span>
            </div>
            <motion.div animate={balanceChange ? { scale: [1, 1.04, 1] } : {}}
              className={`mt-5 ${balanceChange === 'increase' ? 'text-[#2ecf6e]'
                : balanceChange === 'decrease' ? 'text-[#E5484D]' : 'text-white'}`}>
              {/* ⚠️ AKTİF cüzdanın bakiyesi, kendi biriminde. user.balance HER
                  ZAMAN site parasıdır ve .toFixed(2) ondalık basıyordu. */}
              <span className="mm-num font-bold text-4xl sm:text-5xl md:text-6xl leading-none">
                <WalletValue amount={balance} wallet={wallet} />
              </span>
            </motion.div>
            <div className="flex gap-2.5 mt-8">
              <Button onClick={() => setWalletOpen(true)}
                className="font-heading text-sm font-bold uppercase tracking-wider bg-[#7b2fc4] hover:bg-[#B059E8] text-white px-7 transition-colors">
                Deposit
              </Button>
              <Button onClick={() => setWalletOpen(true)} variant="outline"
                className="font-heading text-sm font-semibold uppercase tracking-wider border-[#2A2D31] hover:border-[#3a3d43] text-zinc-300 px-7">
                Withdraw
              </Button>
            </div>
          </motion.div>

          {/* Level Card */}
          <motion.div className="md:col-span-4 mm-panel p-6 flex flex-col justify-center">
            <div className="flex items-end justify-between gap-3 mb-5">
              <div>
                <span className="mm-eyebrow">Level</span>
                <p className="mm-num font-bold text-4xl text-white leading-none mt-2">{currentLevel}</p>
              </div>
              <Trophy className="w-5 h-5 text-[#F5A524] shrink-0 mb-1" />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-baseline">
                <span className="mm-eyebrow">XP</span>
                <span className="mm-num text-[12px] text-zinc-300">
                  {currentXp % 1 !== 0 ? currentXp.toFixed(1) : currentXp} / {requiredXp}
                </span>
              </div>
              <div className="h-2 bg-[#121417] rounded-full overflow-hidden border border-[#2A2D31]">
                <motion.div initial={{ width: 0 }} animate={{ width: `${levelProgress}%` }}
                  transition={{ duration: 1 }} className="h-full bg-gradient-to-r from-[#7b2fc4] to-[#c98bf0]" />
              </div>
              <p className="text-[11px] text-zinc-500">
                <span className="mm-num font-bold text-zinc-300">
                  {remainingXp % 1 !== 0 ? remainingXp.toFixed(1) : remainingXp} XP
                </span> to the next level
              </p>
            </div>
          </motion.div>

          <div className="md:col-span-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mt-2">
            {/* ⚠️ "$" İŞARETİ SÖKÜLDÜ — SİTEDE DOLAR DİYE BİR ŞEY YOK.
                Bu kartlar "1234 $" yazıyordu ama site parasının adı MM Coin
                ve her yerde kendi madeni parasıyla gösteriliyor. Kullanıcı
                aynı bakiyeyi header'da MM Coin, burada dolar olarak görünce
                "hangisi gerçek?" diye soruyordu. `WalletValue` doğru simgeyi
                cüzdana göre kendisi seçiyor — biçimlendirme tek kaynakta.
                ⚠️ Alt metinlerdeki "Robux wallet" de bayattı: o ray
                kaldırıldı, yerine MM2 eşya rayı geldi. */}
            <StatCard icon={TrendingUp} title="Net profit" delay={0.2}
              value={<WalletValue amount={stats?.net_profit || 0} wallet="main" size={14} />}
              subtitle="All time"
              valueColor={(stats?.net_profit || 0) >= 0 ? "text-[#2ecf6e]" : "text-[#E5484D]"} />
            <StatCard icon={ArrowDownToLine} title="Deposited" delay={0.3}
              value={<WalletValue amount={stats?.total_deposited || 0} wallet="main" size={14} />}
              subtitle="MM2 items" />
            <StatCard icon={ArrowUpFromLine} title="Withdrawn" delay={0.4}
              value={<WalletValue amount={stats?.total_withdrawn || 0} wallet="main" size={14} />}
              subtitle="MM2 items" />
            {/* 🍀 LC RAYI AYRI GÖSTERİLİR.
                ⚠️ Yukarıdaki iki kart YALNIZCA MM2 eşya işlemlerini sayar;
                kripto işlemleri farklı tipte VE farklı BİRİMDE (mili-LC).
                Aynı karta toplamak site parasıyla gerçek parayı toplamak
                olurdu — backend de bu yüzden ayrı alanlarda gönderiyor. */}
            {(stats?.total_deposited_lc > 0 || stats?.total_withdrawn_lc > 0 || stats?.lc_balance_milli > 0) && (<>
              <StatCard icon={ArrowDownToLine} title={`Deposited · ${BB_NAME}`} delay={0.35}
                value={<WalletValue amount={stats?.total_deposited_lc || 0} wallet="lc" size={14} />}
                subtitle="Crypto" valueColor="text-[#F5B700]" />
              <StatCard icon={ArrowUpFromLine} title={`Withdrawn · ${BB_NAME}`} delay={0.45}
                value={<WalletValue amount={stats?.total_withdrawn_lc || 0} wallet="lc" size={14} />}
                subtitle="Crypto" valueColor="text-[#F5B700]" />
              <StatCard icon={TrendingUp} title={`Net · ${BB_NAME}`} delay={0.55}
                value={<WalletValue amount={stats?.net_profit_lc || 0} wallet="lc" size={14} />}
                subtitle="Real-money rail"
                valueColor={(stats?.net_profit_lc || 0) >= 0 ? "text-[#2ecf6e]" : "text-[#E5484D]"} />
            </>)}
            <StatCard icon={Zap} title="Games played" delay={0.5}
              value={(stats?.total_games || 0).toLocaleString()} subtitle="Total rounds" />
            {/* Backend bu toplamı bet_units'ten üretir: LC bahisleri SİTE
                EŞDEĞERİNE çevrilmiş gelir, iki cüzdan karşılaştırılabilir. */}
            <StatCard icon={Activity} title="Total wagered" delay={0.6}
              value={<WalletValue amount={stats?.total_wagered || 0} wallet="main" size={14} />}
              subtitle="Both wallets, site equivalent" />
            <StatCard icon={Trophy} title="Win rate" delay={0.7}
              value={`${winRate}%`} subtitle={`${stats?.total_wins || 0} rounds won`} />
          </div>

          {/* YENİ: SON 20 OYUN + SON 20 PARA İŞLEMİ */}
          <ActivityPanel
            status={historyStatus}
            games={history.games}
            transactions={history.transactions}
            activeTab={historyTab}
            onTabChange={setHistoryTab}
            onRefresh={() => fetchHistory({ silent: historyStatus === 'ready' })}
            refreshing={historyRefreshing}
            onDeposit={() => setWalletOpen(true)}
            reduceMotion={reduceMotion}
          />
        </div>
      </div>

      {/* ROBLOX KULLANICI ADI MODALI (opsiyonel · kozmetik · doğrulama yok) */}
      <AnimatePresence>
        {robloxModal && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={() => setRobloxModal(false)}>
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              className="bg-[#1A1D20] border border-[#B059E8]/40 rounded-2xl p-6 max-w-md w-full relative shadow-[0_0_50px_rgba(176,89,232,0.2)]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Gamepad2 className="w-6 h-6 text-[#c98bf0]" />
                  <h2 className="text-xl font-bold text-white">Roblox Username</h2>
                </div>
                <button onClick={() => setRobloxModal(false)} className="p-2 text-zinc-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="bg-[#15171a] border border-[#2A2D31] rounded-xl p-3 mb-4 flex items-start gap-2.5">
                <Info className="w-4 h-4 text-zinc-500 shrink-0 mt-0.5" />
                <p className="text-xs text-zinc-400 leading-snug">
                  Optional and cosmetic — this only sets the avatar shown on your profile. No verification code needed.
                </p>
              </div>

              <div className="space-y-2 mb-2">
                <label className="text-xs text-zinc-500 uppercase tracking-widest font-bold">Roblox Username</label>
                <div className="relative">
                  <input
                    type="text"
                    value={robloxInput}
                    onChange={(e) => setRobloxInput(e.target.value)}
                    placeholder="e.g. builderman"
                    className="w-full p-3.5 bg-[#15171a] border border-[#2A2D31] rounded-xl text-white outline-none focus:border-[#B059E8] transition-colors"
                  />
                  {robloxChecking && <Loader2 className="w-4 h-4 text-zinc-500 animate-spin absolute right-3.5 top-1/2 -translate-y-1/2" />}
                </div>
              </div>

              {robloxPreview && (
                <div className="flex items-center gap-3 p-2.5 bg-[#B059E8]/10 border border-[#B059E8]/25 rounded-xl mb-2">
                  {/* Roblox avatarı yüklenmezse (moderasyonda / henüz üretilmemiş)
                      baş harfe düşülür. Herkese aynı gri görseli göstermek,
                      farklı hesapları aynı kişi gibi gösterir. */}
                  {robloxPreview.avatar_url && !previewImgFailed ? (
                    <img
                      src={robloxPreview.avatar_url}
                      alt="" className="w-10 h-10 rounded-lg bg-[#121417] object-cover shrink-0"
                      onError={() => setPreviewImgFailed(true)}
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#7b2fc4] to-[#B059E8] flex items-center justify-center font-bold text-white shrink-0">
                      {[...((robloxPreview.roblox_username || robloxInput || '?').trim())][0].toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="text-sm text-white font-bold truncate">{robloxPreview.roblox_username || robloxInput}</p>
                    <p className="text-[11px] text-zinc-500">This avatar will be shown on your profile</p>
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-3 pt-3">
                <Button onClick={handleSetRoblox} disabled={actionLoading || !robloxInput.trim()} className="w-full bg-[#7b2fc4] hover:bg-[#B059E8] text-white font-bold shadow-lg py-6">
                  {actionLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Save"}
                </Button>
                {user.roblox_username && (
                  <Button onClick={handleClearRoblox} disabled={actionLoading} variant="outline" className="w-full border-rose-500/30 text-rose-400 hover:bg-rose-500/10 font-bold flex items-center justify-center gap-2">
                    <Trash2 className="w-4 h-4" /> Remove Roblox Username
                  </Button>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

            <WalletModal
        isOpen={walletOpen}
        onClose={() => {
          setWalletOpen(false);
          // Cüzdan kapandığında yeni bir yatırma/çekme kaydı oluşmuş olabilir.
          fetchHistory({ silent: true });
        }}
      />
    </div>
  );
};